// ImageGallery Component - Image Gallery for Place Detail

import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface ImageGalleryProps {
  images: string[];
  placeName: string;
}

export function ImageGallery({ images, placeName }: ImageGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Use placeholder if no images
  const displayImages = images.length > 0
    ? images
    : ['https://placehold.co/800x600/e0e7ff/6366f1?text=GoodPlace'];

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  // Single image layout
  if (displayImages.length === 1) {
    return (
      <>
        <div
          className="aspect-[16/9] md:aspect-[2/1] overflow-hidden rounded-xl cursor-pointer"
          onClick={() => openLightbox(0)}
        >
          <img
            src={displayImages[0]}
            alt={placeName}
            className="w-full h-full object-cover hover:scale-105 transition duration-500"
          />
        </div>
        {lightboxOpen && (
          <Lightbox
            images={displayImages}
            currentIndex={currentIndex}
            onClose={closeLightbox}
            onPrevious={goToPrevious}
            onNext={goToNext}
          />
        )}
      </>
    );
  }

  // Grid layout for multiple images
  return (
    <>
      {/* Mobile: Nur Hauptbild mit Indikator */}
      <div className="md:hidden">
        <div
          className="aspect-[16/9] overflow-hidden rounded-xl cursor-pointer relative"
          onClick={() => openLightbox(0)}
        >
          <img
            src={displayImages[0]}
            alt={`${placeName} - Hauptbild`}
            className="w-full h-full object-cover hover:scale-105 transition duration-500"
          />
          {/* Bilder-Anzahl Indikator */}
          <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full font-medium">
            1 / {displayImages.length}
          </div>
        </div>
      </div>

      {/* Desktop: Grid Layout */}
      <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 rounded-xl overflow-hidden h-[400px]">
        {/* Main Image */}
        <div
          className="col-span-2 row-span-2 cursor-pointer overflow-hidden"
          onClick={() => openLightbox(0)}
        >
          <img
            src={displayImages[0]}
            alt={`${placeName} - Hauptbild`}
            className="w-full h-full object-cover hover:scale-105 transition duration-500"
          />
        </div>

        {/* Secondary Images */}
        {displayImages.slice(1, 5).map((image, index) => (
          <div
            key={index}
            className="cursor-pointer overflow-hidden relative"
            onClick={() => openLightbox(index + 1)}
          >
            <img
              src={image}
              alt={`${placeName} - Bild ${index + 2}`}
              className="w-full h-full object-cover hover:scale-105 transition duration-500"
            />
            {/* Show more badge on last visible image */}
            {index === 3 && displayImages.length > 5 && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="text-white font-semibold">
                  +{displayImages.length - 5} mehr
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={displayImages}
          currentIndex={currentIndex}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
        />
      )}
    </>
  );
}

// Lightbox Component
interface LightboxProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

function Lightbox({ images, currentIndex, onClose, onPrevious, onNext }: LightboxProps) {
  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrevious();
      if (e.key === 'ArrowRight') onNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrevious, onNext]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <span className="text-white">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="p-2 text-white hover:bg-white/10 rounded-full transition"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main Image Area */}
      <div className="flex-1 flex items-center justify-center relative px-16">
        {/* Previous Button */}
        {images.length > 1 && (
          <button
            onClick={onPrevious}
            className="absolute left-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        )}

        {/* Image */}
        <img
          src={images[currentIndex]}
          alt={`Bild ${currentIndex + 1}`}
          className="max-h-full max-w-full object-contain"
        />

        {/* Next Button */}
        {images.length > 1 && (
          <button
            onClick={onNext}
            className="absolute right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        )}
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="p-4 flex gap-2 justify-center overflow-x-auto no-scrollbar">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => {
                // Update currentIndex directly through parent
                const diff = index - currentIndex;
                if (diff > 0) {
                  for (let i = 0; i < diff; i++) onNext();
                } else {
                  for (let i = 0; i < Math.abs(diff); i++) onPrevious();
                }
              }}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition ${
                index === currentIndex ? 'border-white' : 'border-transparent opacity-50'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageGallery;
